import type { PartnerSpecializationFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerSpecializationsQuery';
import { sortAndFilterSpecializations } from 'bundles/partners-graphql/components/cds/specializationUtils';

import _t from 'i18n!nls/partners-graphql';

const XDP_PRODUCT_TYPE = {
  COURSE: 'COURSE',
  SPECIALIZATION: 'SPECIALIZATION',
} as const;

export function toXdpProductId(productType: keyof typeof XDP_PRODUCT_TYPE, productId: string): string {
  return `${XDP_PRODUCT_TYPE[productType]}~${productId}`;
}

type BuildPartnerCourseEnrollmentXdpIdsParams = {
  courseIds: string[];
};

export function buildPartnerCourseEnrollmentXdpIds({ courseIds }: BuildPartnerCourseEnrollmentXdpIdsParams): string[] {
  return courseIds.map((courseId) => toXdpProductId('COURSE', courseId));
}

export type PartnerPageStatType = 'number' | 'rating';

export type PartnerPageStat = {
  id: string;
  title: string;
  value: string;
  type: PartnerPageStatType;
};

export type PartnerPageSchemaPropertyValue = {
  '@type': 'PropertyValue';
  name: string;
  value: number;
};

type RatedItem = {
  rating?: number | null;
  reviewCount?: number | null;
};

export function computeWeightedAverageRating(items: RatedItem[]): number | null {
  let weightedSum = 0;
  let totalReviews = 0;

  items.forEach(({ rating, reviewCount }) => {
    if (rating != null && reviewCount != null && reviewCount > 0) {
      weightedSum += rating * reviewCount;
      totalReviews += reviewCount;
    }
  });

  if (totalReviews === 0) {
    return null;
  }

  return weightedSum / totalReviews;
}

type PartnerEnrollmentElement = {
  xdpMetadata?: {
    __typename?: string;
    cdpMetadata?: { totalEnrollmentCount?: number | null } | null;
  } | null;
} | null;

export function sumPartnerCourseEnrollmentCount(enrollmentElements: PartnerEnrollmentElement[]): number {
  return enrollmentElements.reduce((total, element) => {
    if (element?.xdpMetadata?.__typename !== 'XdpV1_cdpMetadataMember') {
      return total;
    }

    return total + (element.xdpMetadata.cdpMetadata?.totalEnrollmentCount ?? 0);
  }, 0);
}

type CourseForStats = RatedItem;

type GetPartnerPageStatsParams = {
  courses: CourseForStats[];
  courseCount?: number;
  specializations: PartnerSpecializationFragment[];
  learnerCount?: number | null;
};

export function formatPartnerStatNumber(value: number): string {
  return value.toLocaleString(_t.getLocale());
}

export function getPartnerPageStats({
  courses,
  courseCount,
  specializations,
  learnerCount,
}: GetPartnerPageStatsParams): PartnerPageStat[] {
  const filteredSpecializations = sortAndFilterSpecializations(specializations);
  const certificatesAndSpecializationsCount = filteredSpecializations.length;
  const coursesCount = courseCount ?? courses.length;
  const averageRating = computeWeightedAverageRating([
    ...courses,
    ...filteredSpecializations.map((specialization) => ({
      rating: specialization.rating?.averageFiveStarRating,
      reviewCount: specialization.rating?.ratingCount,
    })),
  ]);

  const stats: PartnerPageStat[] = [];

  if (coursesCount > 0) {
    stats.push({
      id: 'courses',
      title: _t('Courses'),
      value: formatPartnerStatNumber(coursesCount),
      type: 'number',
    });
  }

  if (certificatesAndSpecializationsCount > 0) {
    stats.push({
      id: 'certificates-and-specializations',
      title: _t('Certificates & Specializations'),
      value: formatPartnerStatNumber(certificatesAndSpecializationsCount),
      type: 'number',
    });
  }

  if (learnerCount != null && learnerCount > 0) {
    stats.push({
      id: 'learners',
      title: _t('Learners'),
      value: formatPartnerStatNumber(learnerCount),
      type: 'number',
    });
  }

  if (averageRating != null) {
    stats.push({
      id: 'average-rating',
      title: _t('Average Rating'),
      value: averageRating.toFixed(1),
      type: 'rating',
    });
  }

  return stats;
}

const PARTNER_PAGE_SCHEMA_STAT_NAMES = {
  courses: 'Courses',
  'certificates-and-specializations': 'Certificates & Specializations',
  learners: 'Learners',
  'average-rating': 'Average Rating',
} as const;

export function getPartnerPageSchemaAdditionalProperties({
  courses,
  courseCount,
  specializations,
  learnerCount,
}: GetPartnerPageStatsParams): PartnerPageSchemaPropertyValue[] {
  const filteredSpecializations = sortAndFilterSpecializations(specializations);
  const certificatesAndSpecializationsCount = filteredSpecializations.length;
  const coursesCount = courseCount ?? courses.length;
  const averageRating = computeWeightedAverageRating([
    ...courses,
    ...filteredSpecializations.map((specialization) => ({
      rating: specialization.rating?.averageFiveStarRating,
      reviewCount: specialization.rating?.ratingCount,
    })),
  ]);

  const properties: PartnerPageSchemaPropertyValue[] = [];

  if (coursesCount > 0) {
    properties.push({
      '@type': 'PropertyValue',
      name: PARTNER_PAGE_SCHEMA_STAT_NAMES.courses,
      value: coursesCount,
    });
  }

  if (certificatesAndSpecializationsCount > 0) {
    properties.push({
      '@type': 'PropertyValue',
      name: PARTNER_PAGE_SCHEMA_STAT_NAMES['certificates-and-specializations'],
      value: certificatesAndSpecializationsCount,
    });
  }

  if (learnerCount != null && learnerCount > 0) {
    properties.push({
      '@type': 'PropertyValue',
      name: PARTNER_PAGE_SCHEMA_STAT_NAMES.learners,
      value: learnerCount,
    });
  }

  if (averageRating != null) {
    properties.push({
      '@type': 'PropertyValue',
      name: PARTNER_PAGE_SCHEMA_STAT_NAMES['average-rating'],
      value: Number(averageRating.toFixed(1)),
    });
  }

  return properties;
}
